<template>
  <div>
    <form class="" @submit.prevent="createAccountingNote">
      <h4 v-translate>Account</h4>
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Account for</label>
            <b-form-radio-group stacked v-model="accountSearchType">
              <b-form-radio value="user"><translate>User</translate></b-form-radio>
              <b-form-radio value="company"><translate>Company</translate></b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group v-show="accountSearchType === 'company'"
            ><br />
            <company-autocomplete
              ref="companyAccountAc"
              @selected="onAccountSearchCompanySelected"
              :includes="['account', 'settings']"
            />
            <span v-if="accountOwner && accountingNote.account_id">
              <span v-translate="{ data: accountOwner }">Company: <strong>%{data}</strong></span
              ><br />
              <span v-translate="{ data: accountingNote.account_id }">Account ID: <strong>%{data}</strong></span>
              <span class="text-success">✔</span>
            </span>
            <span v-else-if="accountOwner && !accountingNote.account_id">
              <span v-translate="{ data: accountOwner }">Company: <strong>%{data}</strong></span
              ><br />
              <small class="text-danger" v-translate>Warning: No account ID!</small>
            </span>
          </b-form-group>
          <b-form-group v-show="accountSearchType === 'user'">
            <br />
            <user-autocomplete ref="userAccountAc" @selected="onAccountSearchUserSelected" :includes="['account']" />
            <span v-if="accountOwner && accountingNote.account_id">
              <span v-translate="{ data: accountOwner }">User: <strong>%{data}</strong></span
              ><br />
              <span v-translate="{ data: accountingNote.account_id }">Account ID: <strong>%{data}</strong></span>
              <span class="text-success">✔</span>
            </span>
            <span v-else-if="accountOwner && !accountingNote.account_id">
              <span v-translate="{ data: accountOwner }">User: <strong>%{data}</strong></span
              ><br />
              <small class="text-danger" v-translate>Warning: No account ID!</small>
            </span>
          </b-form-group>
        </b-col>
      </b-row>
      <b-alert variant="danger" :show="!accountingNote.account_id">
        <translate>No company/user account selected yet</translate>
      </b-alert>
      <hr />
      <h4 v-translate>Note Data</h4>
      <b-row>
        <b-col cols="3">
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Relationship</label>
            <b-form-radio-group stacked v-model="accountingNote.relationship">
              <b-form-radio value="client"><translate>Client</translate></b-form-radio>
              <b-form-radio value="provider"><translate>Provider</translate></b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-col>
        <b-col cols="9">
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Operation</label>
            <b-form-radio-group stacked v-model="accountingNote.type">
              <b-form-radio value="credit"
                ><span
                  v-translate="{
                    relationship: $gettext(
                      accountingNote.relationship.charAt(0).toUpperCase() +
                        accountingNote.relationship.slice(1).toLowerCase()
                    )
                  }"
                  >Credit (Give money to the %{relationship})</span
                ></b-form-radio
              >
              <b-form-radio value="debit"
                ><span
                  v-translate="{
                    relationship: $gettext(
                      accountingNote.relationship.charAt(0).toUpperCase() +
                        accountingNote.relationship.slice(1).toLowerCase()
                    )
                  }"
                  >Debit (Take money from the %{relationship})</span
                ></b-form-radio
              >
            </b-form-radio-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <p>
            <strong v-show="accountingNote.relationship === 'client' && accountingNote.type === 'credit'" v-translate
              >You are giving money to the client</strong
            >
            <strong v-show="accountingNote.relationship === 'client' && accountingNote.type === 'debit'" v-translate
              >You are taking money from the client</strong
            >
            <strong v-show="accountingNote.relationship === 'provider' && accountingNote.type === 'credit'" v-translate
              >You are giving money to the provider</strong
            >
            <strong v-show="accountingNote.relationship === 'provider' && accountingNote.type === 'debit'" v-translate
              >You are taking money from the provider</strong
            >
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <b-form-checkbox
              v-model="accountingNote.is_outsourcing"
              :disabled="!owner.settings || owner.settings?.findIndex(x => x.name === 'provider_platform_id') === -1"
              switch
              ><translate>3P</translate></b-form-checkbox
            >
          </b-form-group>
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Amount</label>
            <b-input-group>
              <b-form-input v-model="accountingNote.amount" type="number" step="0.0001" required></b-form-input>
              <template #append>
                <b-input-group-text>{{ accountingNote.currency || '-' }}</b-input-group-text>
              </template>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col cols="4"> </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <label for="invalidation-reference" v-translate>Reference</label>
            <textarea
              id="accounting-note-reference"
              ref="accountingNoteReference"
              v-model="accountingNote.reference"
              type="text"
              class="mb-2 col-12 form-control"
              required
            />
          </b-form-group>
        </b-col>
        <b-col cols="4">
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Take</label>
            <b-form-radio-group stacked v-model="accountingNote.visibility">
              <b-form-radio value="all"><translate>YES</translate></b-form-radio>
              <b-form-radio value="internal"><translate>NO</translate></b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-col>
      </b-row>
      <hr />
      <h4 v-translate>Note Optionals</h4>
      <b-row>
        <b-col cols="6">
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Attach to Shipping ID</label>
            <b-form-input v-model="accountingNote.shipping_id" type="text"></b-form-input>
            <b-form-text v-translate>Shipping ID must be valid</b-form-text>
          </b-form-group>
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Attach to Service ID</label>
            <b-form-select v-model="accountingNote.service_id" :options="servicesSelect">
              <template #first>
                <option :value="null" v-translate>Select a Service</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group :class="isFutureImputationDate ? 'bg-warning-light p-1' : ''">
            <label for="invalidation-imputation-date" v-translate>Imputation Date</label>
            <b-form-datepicker
              v-model="accountingNote.imputation_date"
              locale="es"
              hide-header
              size="sm"
              reset-button
              close-button
              :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
              v-bind="{}"
              :min="minAccountingNoteImputationDate"
              :max="maxAccountingNoteImputationDate"
              :placeholder="$gettext('No date selected')"
            ></b-form-datepicker>
            <b-form-text v-translate>If it's different from now</b-form-text>
            <small
              class="form-text text-danger"
              v-if="showLastCut"
              v-translate="{ remoteLastCut: moment(remoteLastCut).format('L') }"
            >
              You cannot set an Imputation Date prior to the last accounting close on %{ remoteLastCut }. If it is the
              case, speak to administration/finance explaining the situation so they can take action
            </small>
            <b-alert variant="warning" :show="isFutureImputationDate" class="p-0">
              <strong v-translate>You've selected a FUTURE Imputation Date, is it OK?</strong>
            </b-alert>
          </b-form-group>
          <b-form-group>
            <label for="invalidation-imputation-date" v-translate>Attach to Fleet Rental Order ID</label>
            <b-form-input v-model="accountingNote.fleet_rental_order_id" type="text"></b-form-input>
            <b-form-text v-translate>Fleet Rental Order ID must be valid</b-form-text>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="text-right form-group col-12 form-actions">
        <b-button type="submit" variant="primary" size="lg" :disabled="!accountingNoteReady" v-translate
          >Create Note</b-button
        >
        <b-button type="button" variant="outline-primary" size="lg" @click.prevent="$emit('cancel')" v-translate
          >Cancel</b-button
        >
      </div>
    </form>
  </div>
</template>

<script>
import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import { getCountries } from '@/services/country'

export default {
  name: 'AccountingNoteForm',
  components: {
    CompanyAutocomplete,
    UserAutocomplete
  },
  props: {
    type: {
      type: String,
      default: 'credit'
    }
  },
  data() {
    return {
      accountingNote: {
        account_id: null,
        type: 'credit',
        amount: null,
        currency: '',
        shipping_id: null,
        service_id: null,
        fleet_rental_order_id: null,
        relationship: 'client',
        reference: '',
        visibility: 'all',
        imputation_date: null,
        status: 'pending',
        is_outsourcing: false
      },
      accountSearchType: 'user',
      accountOwner: '',
      currencySelect: [],
      minAccountingNoteImputationDate: this.moment().subtract(1, 'month').format('YYYY-MM-DD'),
      maxAccountingNoteImputationDate: this.moment().add(5, 'month').format('YYYY-MM-DD'),
      mustBeApproved: true,
      owner: {},
      remoteLastCut: null
    }
  },
  computed: {
    accountingNoteReady() {
      return (
        this.accountingNote.account_id &&
        this.accountingNote.amount &&
        this.accountingNote.type &&
        this.accountingNote.currency &&
        this.accountingNote.relationship &&
        this.accountingNote.reference
      )
    },
    isFutureImputationDate() {
      return this.accountingNote.imputation_date && this.moment().isBefore(this.accountingNote.imputation_date)
    },
    showLastCut() {
      return !!this.remoteLastCut
    }
  },
  watch: {
    accountSearchType(val) {
      this.accountOwner = ''
      this.accountingNote.account_id = null
      this.accountingNote.currency = ''
    },
    async owner(val) {
      if (!this.isAccountant) {
        const country = await this.$http.get(this.$appConfig.api.baseUrl + 'countries', {
          params: {
            filter_groups: [
              {
                filters: [['iso_code', 'eq', val.company?.country_iso_code || val.country_iso_code]]
              }
            ]
          }
        })
        this.remoteLastCut = country.data.data[0]?.front_config?.finance?.lastCut || null
        this.minAccountingNoteImputationDate = this.remoteLastCut || this.minAccountingNoteImputationDate
        if (this.isAdmin) {
          this.minAccountingNoteImputationDate = null
          this.maxAccountingNoteImputationDate = null
        } else if (
          !this.moment(this.accountingNote.imputation_date).isSameOrAfter(this.minAccountingNoteImputationDate, 'day')
        ) {
          this.accountingNote.imputation_date = null
        }
      }
    }
  },
  created() {
    this.fetchServices()
  },
  mounted() {
    this.accountingNote.type = this.type
    this.mustBeApproved = !this.isAccountApprover
    if (this.isAdmin) {
      this.minAccountingNoteImputationDate = null
      this.maxAccountingNoteImputationDate = null
    }
  },
  methods: {
    createAccountingNote() {
      if (
        confirm(
          this.$gettextInterpolate(this.$gettext('Are you sure you want to create the %{type} Accounting Note?'), {
            type: this.accountingNote.type.toUpperCase()
          })
        )
      ) {
        this.accountingNote.shipping_id = this.accountingNote.shipping_id || null
        if (!this.accountingNote.shipping_id) {
          if (
            confirm(
              this.$gettext(
                'The Accounting Note you are creating DOES NOT reference any Shipping ID. This will affect resulting take of the account.\n\nAre you sure you want to continue?'
              )
            )
          ) {
            this.doCreateAccountingNote()
          }
        } else {
          this.doCreateAccountingNote()
        }
      }
    },
    doCreateAccountingNote() {
      if (this.accountingNote.imputation_date) {
        this.accountingNote.imputation_date = this.moment(this.accountingNote.imputation_date + ' ' + '00:00:00')
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      } else {
        delete this.accountingNote.imputation_date
      }

      this.$http.post(this.$appConfig.api.baseUrl + 'accounting-notes', this.accountingNote).then(response => {
        if (!this.mustBeApproved) {
          this.autoApproveNote(response.body.id)
        } else {
          this.$emit('success', true)
        }
      })
    },
    autoApproveNote(id) {
      const payload = {
        status: 'approved',
        review_notes: 'Manually created by FIN'
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'accounting-notes/' + id + '/review', payload).then(response => {
        this.$emit('success', true)
      })
    },
    resetAccountingNote() {
      this.owner = {}
      this.accountOwner = ''
      this.searchCompanyQuery = ''
      this.searchUserQuery = ''
      Object.assign(this.$data.accountingNote, this.$options.data.call(this).accountingNote)
    },
    onAccountSearchCompanySelected(item) {
      this.accountingNote.is_outsourcing = false
      this.owner = item
      this.accountOwner = item.name
      if (item.account) {
        this.accountingNote.account_id = item.account.id
        this.accountingNote.relationship = item.relationship
      } else {
        this.accountingNote.account_id = null
      }
      this.accountingNote.currency = getCountries().find(x => x.iso_code === item.country_iso_code).currency
      this.$refs.companyAccountAc.searchQuery = ''
    },
    onAccountSearchUserSelected(item) {
      this.accountingNote.is_outsourcing = false
      this.owner = item
      this.accountOwner = item.name
      if (item.account) {
        this.accountingNote.account_id = item.account.id
        this.accountingNote.relationship = item.company_id === null ? 'provider' : this.accountingNote.relationship
      } else {
        this.accountingNote.account_id = null
      }
      this.accountingNote.currency = getCountries().find(x => x.iso_code === item.country_iso_code).currency
      this.$refs.userAccountAc.searchQuery = ''
    }
  }
}
</script>
